import React, { useLayoutEffect, useMemo } from 'react';
import { Box, Link as MuiLink, Alert } from '@mui/material';
import { Link as RouterLink, useLocation, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { SEARCE_COLORS } from '../theme';
import { dashboardApi } from '../services/api';
import type {
  ExecutiveSummaryMilestoneRow,
  ExecutiveSummaryOutcomeRow,
  ExecutiveSummaryKpisPayload,
} from '../types/api';
import ProjectHeroCard from '../components/executiveSummary/ProjectHeroCard';
import HealthChips from '../components/executiveSummary/HealthChips';
import ExecutiveSummaryNarrative from '../components/executiveSummary/ExecutiveSummaryNarrative';
import HighlightsLowlights from '../components/executiveSummary/HighlightsLowlights';
import RaidSection from '../components/executiveSummary/RaidSection';
import MilestonesSection from '../components/executiveSummary/MilestonesSection';
import ReferenceLinks from '../components/executiveSummary/ReferenceLinks';
import {
  defaultHeroShell,
  emptyHealthChips,
  emptyMilestones,
} from '../components/executiveSummary/executiveSummaryDefaults';
import type {
  Goal,
  GoalStatus,
  HealthChip,
  HeroData,
  Milestone,
  RagLevel,
  RaidItem,
  RaidType,
  RaidStatus,
  ReferenceLink,
} from '../components/executiveSummary/executiveSummaryTypes';
import type { ExecutiveSummaryRaidItemRaw } from '../types/api';
import { formatDateEnhanced } from '../utils/customerSentimentutils';
import { isMilestoneCompleted } from '../components/executiveSummary/milestoneUtils';

function mapRagLevel(rag: string | null | undefined): RagLevel {
  const u = (rag || '').toLowerCase();
  if (u.includes('red')) return 'red';
  if (u.includes('amber') || u.includes('yellow')) return 'amber';
  return 'green';
}

/** Pill text like the Project Insights mock: Green, Amber, Red. */
function ragPillLabel(rag: string | null | undefined): string {
  const t = (rag || '').trim();
  if (!t) return '—';
  const lower = t.toLowerCase();
  if (lower === 'green' || lower === 'amber' || lower === 'red' || lower === 'yellow') {
    const base = lower === 'yellow' ? 'amber' : lower;
    return base.charAt(0).toUpperCase() + base.slice(1);
  }
  return t.charAt(0).toUpperCase() + t.slice(1);
}

function formatHeroUpdatedDate(raw: string | null | undefined): string {
  if (!raw?.trim()) {
    return new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  }
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) {
    return raw.slice(0, 10);
  }
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

function buildHeroFromProjectRow(
  row: ProjectDetailRow,
  projectCode: string,
  kpis: HeroData['kpis'],
): HeroData {
  const title = String(row.project_name || '').trim() || defaultHeroShell.title;
  const pr = String(row.project_code || '').trim() || projectCode;
  const client = String(row.account || '').trim() || defaultHeroShell.client;
  const ragRaw = row.rag_status != null ? String(row.rag_status) : '';
  const ragLevel = mapRagLevel(ragRaw);
  const lastUpdated = formatHeroUpdatedDate(
    row.last_updated != null ? String(row.last_updated) : undefined
  );
  return {
    ...defaultHeroShell,
    title,
    pr,
    client,
    rag: { level: ragLevel, label: ragPillLabel(ragRaw) },
    tagline: String(row.status || '').trim() || defaultHeroShell.tagline,
    lastUpdated,
    kpis,
    dates: heroDatesFromProjectRow(row),
  };
}

function mapMilestonesFromApi(rows: ExecutiveSummaryMilestoneRow[]): Milestone[] {
  return rows.map((r) => ({
    status: r.status,
    plannedDate: r.planned_date,
    targetShift: r.target_shift,
    milestone: r.milestone,
    billable: r.billable === 'Yes' || r.billable === 'No' ? r.billable : '—',
  }));
}

function mapOutcomeStatusToGoalStatus(raw: string): GoalStatus {
  const s = raw.trim().toLowerCase();
  if (!s) return 'on-track';
  if (s.includes('risk')) return 'at-risk';
  if (s.includes('progress')) return 'in-progress';
  if (s.includes('track')) return 'on-track';
  if (s.includes('done') || s.includes('complete')) return 'done';
  return 'on-track';
}

function mapOutcomesApiToGoals(rows: ExecutiveSummaryOutcomeRow[]): Goal[] {
  return rows.map((r, i) => ({
    id: (r.id && String(r.id).trim()) ? String(r.id) : `outcome-${i}`,
    text: (r.business_outcome && r.business_outcome.trim()) || '—',
    status: mapOutcomeStatusToGoalStatus(r.outcome_status || ''),
    statusLabel: (r.outcome_status || '').trim() || undefined,
    description: (r.description && String(r.description).trim()) || undefined,
  }));
}

/** Turn API description into narrative blocks.
 *  - Plain text: split on blank lines (double newline = new paragraph).
 *  - Rich-text editor output (HTML): keep as a single block so tags don't
 *    get sliced apart; the narrative renderer sanitises and renders it. */
function weeklyDescriptionToParagraphs(text: string | null | undefined): string[] {
  const t = (text ?? '').trim();
  if (!t) return [];
  if (/<[a-z][\s\S]*>/i.test(t)) {
    return [t];
  }
  const blocks = t.split(/\n\n+/).map((p) => p.trim()).filter(Boolean);
  return blocks.length > 0 ? blocks : [t];
}

function fmtPct(val: number | undefined | null): string {
  if (val == null || Number.isNaN(val)) return '—';
  return `${Math.round(val)}%`;
}

function buildHeroKpis(
  kpisData: ExecutiveSummaryKpisPayload | undefined,
  msData: { milestones: { status: string }[] } | undefined,
): HeroData['kpis'] {
  const effortPct = kpisData?.effort_pct;
  const effortMonthlyPct = kpisData?.effort_monthly_pct;
  const timelinePct = kpisData?.timeline_pct;

  let milestonePct: number | undefined;
  if (msData?.milestones && msData.milestones.length > 0) {
    const completed = msData.milestones.filter((m) => isMilestoneCompleted(m.status)).length;
    milestonePct = Math.round((completed / msData.milestones.length) * 100);
  }

  return [
    {
      label: 'Effort',
      color: 'amber',
      value: fmtPct(effortPct),
      description: 'Actual hours logged (Zoho) ÷ Total allocated hours (SFDC)',
    },
    {
      label: 'Effort Monthly',
      color: 'amber',
      value: fmtPct(effortMonthlyPct),
      description: 'Hours logged this month ÷ Pro-rated monthly allocation',
    },
    {
      label: 'Milestone',
      color: 'amber',
      value: fmtPct(milestonePct),
      description: 'Milestones completed ÷ Total milestones planned',
    },
    {
      label: 'Timeline',
      color: 'blue',
      value: fmtPct(timelinePct),
      description: 'Elapsed project days ÷ Total planned project duration',
    },
  ];
}

function mapRaidItemsFromApi(rows: ExecutiveSummaryRaidItemRaw[]): RaidItem[] {
  const counters: Record<string, number> = {};
  return rows.map((r) => {
    const rawType = (r.log_type || 'risk').toLowerCase();
    const type: RaidType = rawType === 'issue' ? 'issue' : rawType === 'action' ? 'action' : 'risk';
    const prefix = type === 'risk' ? 'R' : type === 'issue' ? 'I' : 'A';
    counters[prefix] = (counters[prefix] ?? 0) + 1;
    const shortId = `${prefix}${counters[prefix]}`;

    const clamp = (v: number) => Math.min(3, Math.max(1, v));
    const p: number = clamp(parseInt(r.probability || '1', 10) || 1);
    const i: number = clamp(parseInt(r.impact || '1', 10) || 1);

    const rawStatus = (r.status || 'Active').trim();
    let status: RaidStatus;
    if (rawStatus === 'Active' || rawStatus === 'Open') status = 'Open';
    else if (rawStatus === 'In Progress') status = 'In Progress';
    else if (rawStatus === 'Complete' || rawStatus === 'Completed') status = 'Complete';
    else if (rawStatus === 'Closed') status = 'Closed';
    else status = 'Open';

    const description = (r.description || '').replace(/<[^>]*>/g, '').trim();

    return {
      id: shortId,
      type,
      p,
      i,
      title: r.title || '—',
      obs: description || '—',
      act: r.category ? `Category: ${r.category}` : '',
      owner: r.owner_name || '—',
      due: r.raised_date || '—',
      status,
    };
  });
}

type ProjectDetailRow = Record<string, unknown> & {
  project_code?: string;
  project_name?: string;
  account?: string;
  status?: string;
  rag_status?: string;
  last_updated?: string;
  start_date?: string | null;
  estimated_end_date?: string | null;
  actual_end_date?: string | null;
  repository_link?: string | null;
};

function heroDatesFromProjectRow(row: ProjectDetailRow): HeroData['dates'] {
  return {
    startDate: formatDateEnhanced(
      row.start_date != null ? String(row.start_date) : undefined
    ),
    estimatedEndDate: formatDateEnhanced(
      row.estimated_end_date != null ? String(row.estimated_end_date) : undefined
    ),
    actualEndDate: formatDateEnhanced(
      row.actual_end_date != null ? String(row.actual_end_date) : undefined
    ),
  };
}

/** Accept only validated http(s) URLs so we don't render `javascript:` / mailto / null. */
function sanitizeRepoUrl(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  try {
    const url = new URL(trimmed);
    if (url.protocol === 'http:' || url.protocol === 'https:') return url.toString();
    return null;
  } catch {
    return null;
  }
}

type ExecutiveSummaryLocationState = {
  executiveSummaryProject?: ProjectDetailRow;
};

const ExecutiveSummaryPage: React.FC = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const fromProjectInsights = location.pathname === '/projects/executive-summary';
  const projectCode = (searchParams.get('projectCode') || '').trim();

  const milestonesQuery = useQuery({
    queryKey: ['executive-summary-milestones', projectCode],
    queryFn: () => dashboardApi.getExecutiveSummaryMilestones(projectCode),
    enabled: fromProjectInsights && projectCode.length > 0,
    staleTime: 60_000,
  });

  const outcomesQuery = useQuery({
    queryKey: ['executive-summary-outcomes', projectCode],
    queryFn: () => dashboardApi.getExecutiveSummaryOutcomes(projectCode),
    enabled: fromProjectInsights && projectCode.length > 0,
    staleTime: 60_000,
  });

  const weeklyDescriptionQuery = useQuery({
    queryKey: ['executive-summary-weekly-description', projectCode],
    queryFn: () => dashboardApi.getExecutiveSummaryWeeklyDescription(projectCode),
    enabled: fromProjectInsights && projectCode.length > 0,
    staleTime: 60_000,
  });

  const weeklyHighlightsQuery = useQuery({
    queryKey: ['executive-summary-weekly-highlights', projectCode],
    queryFn: () => dashboardApi.getExecutiveSummaryWeeklyHighlights(projectCode),
    enabled: fromProjectInsights && projectCode.length > 0,
    staleTime: 60_000,
  });

  const weeklyLowlightsQuery = useQuery({
    queryKey: ['executive-summary-weekly-lowlights', projectCode],
    queryFn: () => dashboardApi.getExecutiveSummaryWeeklyLowlights(projectCode),
    enabled: fromProjectInsights && projectCode.length > 0,
    staleTime: 60_000,
  });

  const raidQuery = useQuery({
    queryKey: ['executive-summary-raid', projectCode],
    queryFn: () => dashboardApi.getExecutiveSummaryRaid(projectCode),
    enabled: fromProjectInsights && projectCode.length > 0,
    staleTime: 60_000,
  });

  const kpisQuery = useQuery({
    queryKey: ['executive-summary-kpis', projectCode],
    queryFn: () => dashboardApi.getExecutiveSummaryKpis(projectCode),
    enabled: fromProjectInsights && projectCode.length > 0,
    staleTime: 60_000,
  });

  const detailRow = useMemo((): ProjectDetailRow | null => {
    if (!fromProjectInsights || !projectCode) return null;
    const raw = (location.state as ExecutiveSummaryLocationState | null)?.executiveSummaryProject;
    if (!raw) return null;
    const norm = projectCode.toUpperCase();
    const rowCode = String(raw.project_code ?? '')
      .trim()
      .toUpperCase();
    if (rowCode !== norm) return null;
    return raw;
  }, [fromProjectInsights, projectCode, location.state]);

  /** Load header fields when opened via URL/new tab (no location.state). */
  const projectHeaderQuery = useQuery({
    queryKey: ['executive-summary-project-header', projectCode],
    queryFn: () =>
      dashboardApi.getProjectDetails({
        project_codes: [projectCode],
        page: 1,
        page_size: 1,
      }),
    enabled: fromProjectInsights && projectCode.length > 0 && detailRow === null,
    staleTime: 60_000,
  });

  const apiProjectRow = useMemo((): ProjectDetailRow | null => {
    if (detailRow) return null;
    const projects = projectHeaderQuery.data?.projects;
    if (!projects?.length) return null;
    const norm = projectCode.toUpperCase();
    const match = projects.find(
      (p) =>
        String(p?.project_code ?? '')
          .trim()
          .toUpperCase() === norm
    );
    return (match ?? projects[0]) as ProjectDetailRow;
  }, [detailRow, projectHeaderQuery.data, projectCode]);

  useLayoutEffect(() => {
    if (!fromProjectInsights) return;
    const el = document.getElementById('dashboard-workspace-scroll');
    if (el) {
      el.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }
    window.scrollTo(0, 0);
  }, [fromProjectInsights, projectCode, location.pathname]);

  const msData = milestonesQuery.data;

  const hero: HeroData = useMemo(() => {
    const kpis =
      fromProjectInsights && projectCode ? buildHeroKpis(kpisQuery.data, msData) : [];

    if (!fromProjectInsights || !projectCode) {
      return defaultHeroShell;
    }

    const rowSource = detailRow ?? apiProjectRow;
    if (rowSource) {
      return buildHeroFromProjectRow(rowSource, projectCode, kpis);
    }

    return {
      ...defaultHeroShell,
      title: 'Project',
      pr: projectCode,
      tagline: projectHeaderQuery.isLoading ? '' : defaultHeroShell.tagline,
      kpis,
    };
  }, [
    fromProjectInsights,
    projectCode,
    detailRow,
    apiProjectRow,
    kpisQuery.data,
    msData,
    projectHeaderQuery.isLoading,
    projectHeaderQuery.isFetched,
  ]);

  const milestoneRows = useMemo(() => {
    if (!fromProjectInsights || !projectCode) {
      return emptyMilestones;
    }
    if (msData) {
      return mapMilestonesFromApi(msData.milestones || []);
    }
    if (milestonesQuery.isFetched) {
      return [];
    }
    return [];
  }, [fromProjectInsights, projectCode, msData, milestonesQuery.isFetched]);

  const milestonesError = milestonesQuery.isError ? milestonesQuery.error : null;
  const outcomesError = outcomesQuery.isError ? outcomesQuery.error : null;
  const weeklyZohoError =
    weeklyDescriptionQuery.isError
      ? weeklyDescriptionQuery.error
      : weeklyHighlightsQuery.isError
        ? weeklyHighlightsQuery.error
        : weeklyLowlightsQuery.isError
          ? weeklyLowlightsQuery.error
          : null;

  const outcomeGoals: Goal[] = useMemo(() => {
    if (!fromProjectInsights || !projectCode || outcomesQuery.isError || !outcomesQuery.data) {
      return [];
    }
    return mapOutcomesApiToGoals(outcomesQuery.data.outcomes || []);
  }, [fromProjectInsights, projectCode, outcomesQuery.data, outcomesQuery.isError]);

  const heroGoals: Goal[] = fromProjectInsights && projectCode ? outcomeGoals : [];

  /** Modules-delivered chip when opened from Project Insights (counts from milestone API). */
  const healthChipsForPage: HealthChip[] = useMemo(() => {
    if (!fromProjectInsights || projectCode.length === 0) {
      return emptyHealthChips;
    }
    const modsChip = (emphasis: string): HealthChip[] => [
      { id: 'mods', text: 'Milestones delivered', emphasis, color: 'blue' },
    ];
    if (!milestonesQuery.isFetched) {
      return modsChip('… / …');
    }
    if (!msData?.milestones) {
      return modsChip('0 / 0');
    }
    const completed = msData.milestones.filter((r) => isMilestoneCompleted(r.status)).length;
    const total = msData.milestones.length;
    return modsChip(`${completed} / ${total}`);
  }, [fromProjectInsights, projectCode, milestonesQuery.isFetched, msData]);

  const milestonesSectionLoading = Boolean(
    fromProjectInsights && projectCode.length > 0 && !milestonesQuery.isFetched
  );

  const narrativeParagraphs = useMemo(() => {
    if (!fromProjectInsights || !projectCode) {
      return [];
    }
    if (weeklyDescriptionQuery.isError) {
      return [];
    }
    return weeklyDescriptionToParagraphs(weeklyDescriptionQuery.data?.description);
  }, [
    fromProjectInsights,
    projectCode,
    weeklyDescriptionQuery.data,
    weeklyDescriptionQuery.isError,
  ]);

  const highlightsForPage = useMemo(() => {
    if (!fromProjectInsights || !projectCode) return [];
    if (weeklyHighlightsQuery.isError) return [];
    return weeklyHighlightsQuery.data?.items ?? [];
  }, [fromProjectInsights, projectCode, weeklyHighlightsQuery.data, weeklyHighlightsQuery.isError]);

  const lowlightsForPage = useMemo(() => {
    if (!fromProjectInsights || !projectCode) return [];
    if (weeklyLowlightsQuery.isError) return [];
    return weeklyLowlightsQuery.data?.items ?? [];
  }, [fromProjectInsights, projectCode, weeklyLowlightsQuery.data, weeklyLowlightsQuery.isError]);

  const raidItems: RaidItem[] = useMemo(() => {
    if (!fromProjectInsights || !projectCode || raidQuery.isError || !raidQuery.data) return [];
    return mapRaidItemsFromApi(raidQuery.data.items ?? []);
  }, [fromProjectInsights, projectCode, raidQuery.data, raidQuery.isError]);

  /**
   * "Reference Links" content. Today this is just the project's repository link.
   * Source preference:
   *   1. `detailRow.repository_link` (when navigated with location.state — same-tab opens)
   *   2. `kpisQuery.data.repository_link` (works in new-tab opens, since state isn't carried)
   * If neither yields a valid http(s) URL, the section renders empty.
   */
  const referenceLinksForPage: ReferenceLink[] = useMemo(() => {
    if (!fromProjectInsights || !projectCode) return [];
    const fromRow = sanitizeRepoUrl(detailRow?.repository_link);
    const fromApi = sanitizeRepoUrl(kpisQuery.data?.repository_link);
    const repoUrl = fromRow ?? fromApi;
    if (!repoUrl) return [];
    return [{ label: 'Project Repository', href: repoUrl }];
  }, [fromProjectInsights, projectCode, detailRow?.repository_link, kpisQuery.data?.repository_link]);

  const weeklyDescriptionLoading = Boolean(
    fromProjectInsights && projectCode && weeklyDescriptionQuery.isLoading
  );
  const weeklyHighlightsLoading = Boolean(
    fromProjectInsights && projectCode && weeklyHighlightsQuery.isLoading
  );
  const weeklyLowlightsLoading = Boolean(
    fromProjectInsights && projectCode && weeklyLowlightsQuery.isLoading
  );

  return (
    <Box sx={{ backgroundColor: SEARCE_COLORS.background, minHeight: '100%' }}>
      <Box sx={{ maxWidth: 1320, mx: 'auto', px: { xs: 2, md: 3 }, py: 3 }}>
        {fromProjectInsights && (
          <MuiLink
            component={RouterLink}
            to="/projects"
            underline="hover"
            sx={{
              display: 'inline-block',
              mb: 2,
              fontSize: '0.875rem',
              fontWeight: 600,
              color: SEARCE_COLORS.primary,
            }}
          >
            ← Back to Project Insights
          </MuiLink>
        )}

        {fromProjectInsights && projectCode && milestonesError && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {axios.isAxiosError(milestonesError) &&
            milestonesError.response?.data &&
            typeof (milestonesError.response.data as { detail?: unknown }).detail === 'string'
              ? (milestonesError.response.data as { detail: string }).detail
              : 'Milestones could not be loaded; the table below is empty.'}
          </Alert>
        )}

        {fromProjectInsights && projectCode && outcomesError && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {axios.isAxiosError(outcomesError) &&
            outcomesError.response?.data &&
            typeof (outcomesError.response.data as { detail?: unknown }).detail === 'string'
              ? (outcomesError.response.data as { detail: string }).detail
              : 'Outcomes could not be loaded.'}
          </Alert>
        )}

        {fromProjectInsights && projectCode && weeklyZohoError && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {axios.isAxiosError(weeklyZohoError) &&
            weeklyZohoError.response?.data &&
            typeof (weeklyZohoError.response.data as { detail?: unknown }).detail === 'string'
              ? (weeklyZohoError.response.data as { detail: string }).detail
              : 'Weekly Zoho summary (description, highlights, or lowlights) could not be loaded; those sections appear empty below.'}
          </Alert>
        )}

        <ProjectHeroCard
          hero={hero}
          goals={heroGoals}
          layout="full"
          outcomesLoading={Boolean(
            fromProjectInsights && projectCode && outcomesQuery.isLoading
          )}
        />
        
        <ExecutiveSummaryNarrative
          paragraphs={narrativeParagraphs}
          loading={weeklyDescriptionLoading}
        />
        <HighlightsLowlights
          highlights={highlightsForPage}
          lowlights={lowlightsForPage}
          highlightsLoading={weeklyHighlightsLoading}
          lowlightsLoading={weeklyLowlightsLoading}
        />
        <RaidSection items={raidItems} />
        <MilestonesSection
          milestones={milestoneRows}
          loading={milestonesSectionLoading}
        />
        <HealthChips chips={healthChipsForPage} />
        <ReferenceLinks links={referenceLinksForPage} />
      </Box>
    </Box>
  );
};

export default ExecutiveSummaryPage;
